import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { store } from 'store';
import { logoutAction, refreshTokenAction } from 'store/auth/auth.reducer';
import { AUTH_TOKEN, REFRESH_TOKEN, getFromLocalStorage } from 'utils/localStorage';

const INVALID_TOKEN_MESSAGE = 'auth.invalid_jwt_token';
const EXPIRED_TOKEN_MESSAGE = 'auth.expired_jwt_token';
let _isAlreadyRefreshing = false;
let waitList: Function[] = [];
const { DDOS_GUARD_KEY } = process.env;

const instance = axios.create({
  baseURL: process.env.API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
    'X-Whitelist-Header': DDOS_GUARD_KEY,
  },
});

instance.interceptors.request.use(
  (config) => {
    if (!config.headers?.Authorization) {
      const token = getFromLocalStorage(AUTH_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const refreshToken = async () => {
  if (!_isAlreadyRefreshing) {
    _isAlreadyRefreshing = true;
    const refreshToken = getFromLocalStorage(REFRESH_TOKEN);
    try {
      await store.dispatch(refreshTokenAction({ refreshToken }));
      // resolve waitList
      waitList = waitList.filter((callback) => callback());
    } catch (e) {
      store.dispatch(logoutAction());
      // reject waitList
      waitList = waitList.filter((callback) => callback(e));
    } finally {
      _isAlreadyRefreshing = false;
    }
  }
};

function addToWaitList(originalRequestConfig: AxiosRequestConfig) {
  return new Promise((resolve, reject) => {
    waitList.push((err: any) => {
      if (err) {
        return reject({ error: 'Failed to refresh token' });
      }
      delete originalRequestConfig?.headers?.Authorization;
      resolve(instance.request(originalRequestConfig));
    });
  });
}

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError<any>) => {
    const { response, config, message, code } = error;
    if (!response) {
      return Promise.reject({ message, code });
    }
    const { data, status } = response;
    let errorCode: string | undefined = data?.code;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const json = JSON.parse(text);
        errorCode = json?.code;
      } catch {
      }
    }
    if (config?.headers?.Authorization && status === 401) {
      if (errorCode === INVALID_TOKEN_MESSAGE) {
        store.dispatch(logoutAction());
        return Promise.reject(INVALID_TOKEN_MESSAGE);
      }
      if (errorCode === EXPIRED_TOKEN_MESSAGE) {
        refreshToken();
        return addToWaitList(config);
      }
    }
    return Promise.reject({ data, status });
  }
);

export default instance;
